/**
 * Master Data API
 * Fetches categories, subcategories, and other master data from backend
 */

import { apiClient } from './apiClient';

export interface MasterDataItem {
  _id: string;
  type: string;
  name: string;
  code?: string;
  description?: string;
  metadata?: {
    parentId?: string;
    isActive: boolean;
    order?: number;
  };
}

export interface ServiceCategory {
  category: string;
  subcategories: string[];
  categoryType?: 'local' | 'statewide';
}

/**
 * Fetch master data by type
 */
export async function getMasterDataByType(
  type: string,
  isActive: boolean = true
): Promise<MasterDataItem[]> {
  const params: Record<string, string> = {};
  if (isActive) {
    params.isActive = 'true';
  }

  const response = await apiClient.get<MasterDataItem[]>(`/master-data/type/${type}`, params);

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch master data');
}

/**
 * Fetch service categories with their subcategories from partner-services API
 */
export async function getServiceCategories(search?: string): Promise<ServiceCategory[]> {
  try {
    const params: Record<string, string> = { isActive: 'true' };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const response = await apiClient.get<Array<{
      _id: string;
      category: string;
      subcategories: string[];
      isActive: boolean;
      categoryType?: 'local' | 'statewide';
    }>>('/partner-services', params);

    if (response.success && response.data) {
      const result: ServiceCategory[] = response.data.map((service) => ({
        category: service.category,
        subcategories: service.subcategories.length > 0
          ? service.subcategories
          : [service.category],
        categoryType: service.categoryType || 'local',
      }));

      return result;
    }

    throw new Error(response.message || 'Failed to fetch service categories');
  } catch (error) {
    console.error('Error fetching service categories:', error);
    throw error;
  }
}
